<!-- SEO landing page (target: "invoice ocr" / "ocr invoice", ~2.4K/mo, the money page —
     highest-CPC cluster in the niche: $35-59 CPC, KD 0-35). -->
<template>
  <SeoLanding
    kicker="Invoice OCR"
    h1="Invoice OCR: Automate Invoice Data Entry"
    subtitle="Extract invoice number, vendor, line items, tax, and totals from any invoice — PDF, scan, or photo — and export to JSON, CSV, or your accounting tool. No templates to configure."
    cta-label="Extract an invoice free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="Stop keying invoices by hand"
    closing-subtitle="Upload an invoice, get clean data in seconds. Free for 5 pages a month — no credit card required."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd } from '@/composables/usePageSeo'

const CANONICAL = 'https://www.papersnap.io/invoice-ocr'

const sections = [
  {
    h2: 'What invoice OCR does',
    paragraphs: [
      'Invoice OCR reads an invoice and pulls out the fields your books need — invoice number, vendor, dates, line items, tax, and total — turning a PDF or scan into structured data. The goal is to eliminate manual data entry: instead of a person keying numbers into accounting software, the invoice is read and the fields are extracted automatically.',
      'Traditional invoice OCR relied on per-vendor templates: you had to teach it where each field sat on the page, and a new layout broke it. Papersnap uses document AI instead, so it reads invoices it has never seen before — different vendors, currencies, and formats — without any template setup.',
    ],
  },
  {
    h2: 'What Papersnap pulls from an invoice',
    bullets: [
      { label: 'Header fields', text: 'invoice number, vendor, invoice date, due date, PO number' },
      { label: 'Line items', text: 'description, quantity, unit price, and amount for each row' },
      { label: 'Totals', text: 'subtotal, tax, and grand total — reconciled against the line items' },
      { label: 'Export', text: 'JSON for APIs and integrations, or CSV for spreadsheets and accounting imports' },
    ],
  },
  {
    h2: 'Why template-free matters',
    bullets: [
      { label: 'Works on new vendors', text: 'no setup per supplier — a first-time invoice extracts like any other' },
      { label: 'Handles scans and photos', text: 'OCR reads images; born-digital PDFs are read directly for speed' },
      { label: 'Fewer errors', text: 'no transcription typos on amounts, tax, or dates' },
      { label: 'Scales', text: 'process one invoice or a batch — the same pipeline, no per-layout maintenance' },
    ],
  },
]

const faqs = [
  { q: 'What is invoice OCR?', a: 'Invoice OCR reads an invoice image or PDF and extracts the key fields — invoice number, vendor, line items, tax, and total — as structured data, so you do not have to type them into your accounting system by hand.' },
  { q: 'Does invoice OCR need templates for each vendor?', a: 'Traditional invoice OCR did. Papersnap uses document AI, so it extracts fields from invoices it has never seen — new vendors, currencies, and layouts — with no per-supplier template setup.' },
  { q: 'Can it export invoices to QuickBooks, Xero, or a spreadsheet?', a: 'Papersnap exports clean JSON and CSV. CSV opens directly in Excel or Sheets and imports into most accounting tools; JSON feeds APIs and integrations like Zapier.' },
  { q: 'Does it work on scanned invoices and photos?', a: 'Yes. Scans and phone photos are read via OCR; born-digital PDF invoices are read directly. Both produce the same structured output.' },
  { q: 'Is there a free way to try invoice OCR?', a: 'Yes — Papersnap is free for 5 pages a month with no credit card. Paid plans from $9/month add volume, bulk export, and email support.' },
]

const relatedLinks = [
  { to: '/receipt-scanner', label: 'Receipt scanner' },
  { to: '/pdf-to-csv', label: 'PDF to CSV' },
  { to: '/intelligent-character-recognition', label: 'What is ICR?' },
]

usePageSeo({
  title: 'Invoice OCR — Automate Invoice Data Entry',
  description: 'Template-free invoice OCR: extract invoice number, vendor, line items, tax, and totals from any invoice (PDF, scan, or photo) to JSON, CSV, or your accounting tool.',
  path: '/invoice-ocr',
  jsonLd: [
    articleJsonLd({ headline: 'Invoice OCR: Automate Invoice Data Entry', description: 'How template-free invoice OCR extracts invoice fields to JSON, CSV, or your accounting tool.', url: CANONICAL, datePublished: '2026-07-08' }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
  ],
})
</script>
